import { useEffect, useMemo, useState } from 'react';
import type { Dipendente, Evento } from '../api/tipi.ts';
import { ErroreRisposta, api } from '../api/client.ts';
import stili from './DaDecidere.module.css';

/**
 * Le richieste che aspettano una decisione.
 *
 * Esiste perche' in questo sistema le approvazioni avvengono da sole dopo il
 * preavviso: il "si" e' il comportamento predefinito, e l'unica decisione che
 * qualcuno prende davvero e' il "no". Se quel no dovesse cercarselo in mezzo
 * al calendario non lo prenderebbe mai in tempo.
 *
 * L'ordine e' quello dell'urgenza: prima cio' che non matura da solo, poi
 * cio' che scade stanotte, poi il resto. Chi apre questa pagina alle 18:00
 * deve trovare in cima le righe su cui il tempo sta finendo.
 */

export interface ProprietaDaDecidere {
  dipendenti: readonly Dipendente[];
  eventi: readonly Evento[];
  /** Chi guarda: non decide sulle proprie richieste. */
  dipendenteProprio: string;
  onDecisa: () => Promise<void>;
  /**
   * Ricarica i dati dal server.
   *
   * La pagina non chiedeva mai dati nuovi: mostrava quelli presi all'apertura
   * dell'applicazione. Un responsabile che l'aveva aperta alle 09:21 non
   * vedeva le sei richieste arrivate fra le 09:40 e le 10:22, e aveva
   * davanti "Nessuna richiesta in attesa" mentre l'amministratore, entrato
   * dopo, le vedeva tutte. Sembrava un problema di permessi, ed era un
   * elenco fermo.
   */
  onAggiorna: () => Promise<void>;
}

/** Ogni quanto si ricarica l'elenco mentre la pagina e' aperta. */
const INTERVALLO_AGGIORNAMENTO_MS = 60_000;

interface Riga {
  evento: Evento;
  dipendente: Dipendente;
  /** Giorni al maturare dell'approvazione automatica. Nullo per le urgenti. */
  giorni: number | null;
}

export function DaDecidere({
  dipendenti,
  eventi,
  dipendenteProprio,
  onDecisa,
  onAggiorna,
}: ProprietaDaDecidere) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [motivi, setMotivi] = useState<Record<string, string>>({});
  const [aperta, setAperta] = useState<string | null>(null);
  const [aggiornatoAlle, setAggiornatoAlle] = useState<string | null>(null);

  /*
   * Si aggiorna da sola: all'apertura della pagina, ogni minuto, e quando si
   * torna sulla finestra dopo essere stati altrove.
   *
   * Il ritorno sulla finestra conta piu' dell'intervallo: chi lascia la
   * scheda aperta e ci torna dopo un'ora deve trovare l'elenco di adesso,
   * non aspettare il minuto successivo. L'intervallo non gira a scheda
   * nascosta, dove non lo guarda nessuno.
   *
   * Il motivo di rifiuto che si sta scrivendo sopravvive agli aggiornamenti:
   * sta in uno stato di questa pagina, che non si rimonta.
   */
  useEffect(() => {
    let attivo = true;
    const aggiorna = () => {
      void onAggiorna().then(() => {
        if (attivo) {
          setAggiornatoAlle(
            new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }),
          );
        }
      });
    };
    const seVisibile = () => {
      if (document.visibilityState === 'visible') aggiorna();
    };

    aggiorna();
    const intervallo = window.setInterval(seVisibile, INTERVALLO_AGGIORNAMENTO_MS);
    window.addEventListener('focus', seVisibile);
    document.addEventListener('visibilitychange', seVisibile);
    return () => {
      attivo = false;
      window.clearInterval(intervallo);
      window.removeEventListener('focus', seVisibile);
      document.removeEventListener('visibilitychange', seVisibile);
    };
  }, [onAggiorna]);

  const perId = useMemo(() => new Map(dipendenti.map((d) => [d.id, d])), [dipendenti]);

  const righe = useMemo<Riga[]>(() => {
    return eventi
      .filter((e) => e.stato === 'IN_ATTESA' && e.dataFine >= oggi)
      .map((evento) => {
        const dipendente = perId.get(evento.dipendenteId);
        if (!dipendente) return null;
        return {
          evento,
          dipendente,
          giorni: evento.urgente ? null : giorniTra(oggi, evento.dataInizio),
        };
      })
      .filter((r): r is Riga => r !== null)
      .sort((a, b) => {
        // Le urgenti per prime: non maturano mai da sole.
        if (a.evento.urgente !== b.evento.urgente) return a.evento.urgente ? -1 : 1;
        return a.evento.dataInizio.localeCompare(b.evento.dataInizio);
      });
  }, [eventi, perId, oggi]);

  async function rifiuta(evento: Evento) {
    const motivo = (motivi[evento.id] ?? '').trim();
    if (motivo.length < 3) {
      setErrore('Scrivi un motivo: chi riceve il rifiuto deve capire perché.');
      return;
    }
    setErrore(null);
    setInCorso(evento.id);
    try {
      await api.rifiutaEvento(evento.id, motivo);
      setAperta(null);
      setMotivi((m) => {
        const { [evento.id]: _tolto, ...resto } = m;
        return resto;
      });
      await onDecisa();
    } catch (e) {
      setErrore(e instanceof ErroreRisposta ? e.message : 'Rifiuto non riuscito.');
    } finally {
      setInCorso(null);
    }
  }

  async function approva(evento: Evento) {
    setErrore(null);
    setInCorso(evento.id);
    try {
      await api.approvaEvento(evento.id);
      await onDecisa();
    } catch (e) {
      setErrore(e instanceof ErroreRisposta ? e.message : 'Approvazione non riuscita.');
    } finally {
      setInCorso(null);
    }
  }

  const urgenti = righe.filter((r) => r.evento.urgente).length;

  return (
    <div className={stili.pagina}>
      <header className={stili.testata}>
        <h1 className={stili.titolo}>Da decidere</h1>
        <p className={stili.sottotitolo}>
          {righe.length === 0
            ? 'Nessuna richiesta in attesa.'
            : `${righe.length} ${righe.length === 1 ? 'richiesta' : 'richieste'} in attesa` +
              (urgenti > 0 ? ` · ${urgenti} urgenti` : '')}
        </p>
        {aggiornatoAlle !== null && (
          <p className={stili.aggiornato}>
            Aggiornato alle {aggiornatoAlle} · si aggiorna da solo ogni minuto
          </p>
        )}
      </header>

      {errore && <p className={stili.errore}>{errore}</p>}

      {righe.length === 0 ? (
        <p className={stili.vuoto}>
          Le richieste che rispettano il preavviso si approvano da sole. Qui compaiono
          finché c’è tempo per intervenire.
        </p>
      ) : (
        <ul className={stili.elenco}>
          {righe.map(({ evento, dipendente, giorni }) => {
            const proprio = dipendente.id === dipendenteProprio;
            return (
              <li
                key={evento.id}
                className={`${stili.voce} ${evento.urgente ? stili.voceUrgente : ''}`}
              >
                <div className={stili.intestazioneVoce}>
                  <div>
                    <span className={stili.nome}>{dipendente.nomeCompleto}</span>
                    <span className={stili.collocazione}>
                      {dipendente.sedeNome} · {dipendente.repartoNome}
                    </span>
                  </div>
                  <span className={evento.urgente ? stili.pastigliaUrgente : stili.pastiglia}>
                    {evento.urgente ? 'URGENTE' : scadenza(giorni)}
                  </span>
                </div>

                <div className={stili.dettagli}>
                  <span className={stili.tipo}>{evento.etichetta}</span>
                  <span>{intervallo(evento.dataInizio, evento.dataFine)}</span>
                  {evento.ore !== null && <span>{formattaOre(evento.ore)}</span>}
                  {evento.nota && <span className={stili.nota}>{evento.nota}</span>}
                </div>

                {proprio ? (
                  <p className={stili.aiuto}>
                    È una tua richiesta: per rinunciare annullala dalla tua home.
                  </p>
                ) : aperta === evento.id ? (
                  <div className={stili.riquadroMotivo}>
                    <label className={stili.campo}>
                      <span className={stili.etichetta}>Perché viene rifiutata</span>
                      <textarea
                        className={stili.testo}
                        rows={2}
                        autoFocus
                        placeholder="es. periodo già coperto da altre due assenze nel reparto"
                        value={motivi[evento.id] ?? ''}
                        onChange={(e) =>
                          setMotivi((m) => ({ ...m, [evento.id]: e.target.value }))
                        }
                      />
                      <span className={stili.aiuto}>
                        Il motivo viene mostrato al dipendente sulla sua home.
                      </span>
                    </label>
                    <div className={stili.azioni}>
                      <button
                        type="button"
                        className={stili.secondario}
                        onClick={() => setAperta(null)}
                      >
                        Annulla
                      </button>
                      <button
                        type="button"
                        className={stili.pericolo}
                        disabled={inCorso === evento.id}
                        onClick={() => void rifiuta(evento)}
                      >
                        {inCorso === evento.id ? 'Rifiuto…' : 'Conferma il rifiuto'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={stili.azioni}>
                    <button
                      type="button"
                      className={stili.pericolo}
                      disabled={inCorso !== null}
                      onClick={() => setAperta(evento.id)}
                    >
                      Rifiuta
                    </button>
                    <button
                      type="button"
                      className={stili.primario}
                      disabled={inCorso !== null}
                      onClick={() => void approva(evento)}
                      title="Approva subito, senza aspettare la maturazione"
                    >
                      {inCorso === evento.id ? 'Approvo…' : 'Approva ora'}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Quanto manca all'approvazione automatica, a parole. */
function scadenza(giorni: number | null): string {
  if (giorni === null) return 'in attesa';
  if (giorni <= 0) return 'inizia oggi';
  if (giorni === 1) return 'inizia domani';
  return `fra ${giorni} giorni`;
}

function giorniTra(dal: string, al: string): number {
  return Math.round(
    (Date.parse(`${al}T00:00:00Z`) - Date.parse(`${dal}T00:00:00Z`)) / 86_400_000,
  );
}

function intervallo(dal: string, al: string): string {
  const g = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
  return dal === al ? g(dal) : `${g(dal)} – ${g(al)}`;
}

function formattaOre(ore: number): string {
  const n = String(ore).replace('.', ',');
  return ore === 1 ? `${n} ora` : `${n} ore`;
}
